import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate, useNotify, usePermissions, useRecordContext } from 'react-admin';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { hasPermission } from '../../utils/hasPermission';
import { SiteKioskBayNumberWarning } from './SiteKioskBayNumberWarning';
import { SiteKioskChargeRoundingPanel } from './SiteKioskChargeRoundingPanel';
import { SiteSoepayAccountPanel } from './SiteSoepayAccountPanel';

const SINGULAR_KEY = 'bay.label';
const PLURAL_KEY = 'bay.label_plural';
const OTHER_CHOICE = '__other__';
const MAX_LABEL_LENGTH = 20;

interface KioskSettingDefinition {
    key: string;
    authority: 'cloud' | 'device';
    scopes: string[];
    defaultValue: unknown;
    description: string;
    options?: string[];
}

interface SiteKioskSettingCell {
    key: string;
    value: unknown;
}

interface LabelPreset {
    singular: string;
    plural: string;
}

const asLabel = (value: unknown): string | null =>
    typeof value === 'string' && value.trim().length > 0 ? value : null;

const buildPresets = (
    singularDefinition: KioskSettingDefinition | undefined,
    pluralDefinition: KioskSettingDefinition | undefined
): LabelPreset[] => {
    const singulars = Array.isArray(singularDefinition?.options) ? singularDefinition.options : [];
    const plurals = Array.isArray(pluralDefinition?.options) ? pluralDefinition.options : [];
    const pairs: LabelPreset[] = [];
    for (let index = 0; index < Math.min(singulars.length, plurals.length); index += 1) {
        const singular = asLabel(singulars[index]);
        const plural = asLabel(plurals[index]);
        if (singular && plural) pairs.push({ singular, plural });
    }
    return pairs;
};

const isValidLabel = (value: string): boolean => value.length >= 1 && value.length <= MAX_LABEL_LENGTH;

interface SiteKioskSettingsPanelProps {
    siteId: string;
}

export const SiteKioskSettingsPanel: React.FC<SiteKioskSettingsPanelProps> = ({ siteId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const { permissions, isLoading: permissionsLoading } = usePermissions();

    const permissionList = permissions as string[] | null | undefined;
    const canRead = !permissionsLoading && hasPermission(permissionList, 'kiosk-devices:read:all');
    const canWrite = !permissionsLoading && hasPermission(permissionList, 'kiosk-devices:write:all');

    const settingsUrl = `${API_CONFIG.endpoints.backend.sites}/${siteId}/kiosk-settings`;

    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [definitions, setDefinitions] = useState<KioskSettingDefinition[] | null>(null);
    const [cells, setCells] = useState<SiteKioskSettingCell[]>([]);
    const [singular, setSingular] = useState('');
    const [plural, setPlural] = useState('');
    const [freeText, setFreeText] = useState(false);
    const [loaded, setLoaded] = useState<LabelPreset | null>(null);
    const [validationError, setValidationError] = useState('');
    const [busy, setBusy] = useState(false);

    const translateRef = useRef(translate);
    translateRef.current = translate;

    const reportError = useCallback(
        (error: any) => {
            const apiError = error?.body || error;
            notify(apiError?.message || translateRef.current('resources.sites.kioskSettings.loadError'), { type: 'error' });
        },
        [notify]
    );

    const loadCells = useCallback(async () => {
        const response = await apiClient.get<{ data: SiteKioskSettingCell[] }>(settingsUrl);
        return response.data;
    }, [settingsUrl]);

    useEffect(() => {
        if (!canRead || !siteId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [definitionsResponse, siteCells] = await Promise.all([
                    apiClient.get<{ data: KioskSettingDefinition[] }>(API_CONFIG.endpoints.backend.kioskSettingDefinitions),
                    loadCells(),
                ]);
                if (cancelled) return;
                setDefinitions(definitionsResponse.data);
                setCells(siteCells);
                setLoadFailed(false);
            } catch (error: any) {
                if (cancelled) return;
                setLoadFailed(true);
                reportError(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canRead, siteId, loadCells, reportError]);

    const singularDefinition = definitions?.find((definition) => definition.key === SINGULAR_KEY);
    const pluralDefinition = definitions?.find((definition) => definition.key === PLURAL_KEY);
    const defaultSingular = asLabel(singularDefinition?.defaultValue);
    const defaultPlural = asLabel(pluralDefinition?.defaultValue);

    const presets = useMemo(
        () => buildPresets(singularDefinition, pluralDefinition),
        [singularDefinition, pluralDefinition]
    );

    const singularCell = cells.find((cell) => cell.key === SINGULAR_KEY);
    const pluralCell = cells.find((cell) => cell.key === PLURAL_KEY);
    const overrideCount = (singularCell ? 1 : 0) + (pluralCell ? 1 : 0);
    const changed =
        loaded !== null && (singular.trim() !== loaded.singular || plural.trim() !== loaded.plural);

    useEffect(() => {
        if (!defaultSingular || !defaultPlural) return;
        const overriddenSingular = asLabel(singularCell?.value);
        const overriddenPlural = asLabel(pluralCell?.value);
        const nextSingular = overriddenSingular === null ? defaultSingular : overriddenSingular;
        const nextPlural = overriddenPlural === null ? defaultPlural : overriddenPlural;
        setSingular(nextSingular);
        setPlural(nextPlural);
        setLoaded({ singular: nextSingular.trim(), plural: nextPlural.trim() });
        setFreeText(!presets.some((preset) => preset.singular === nextSingular && preset.plural === nextPlural));
        setValidationError('');
    }, [defaultSingular, defaultPlural, singularCell, pluralCell, presets]);

    const handlePresetChange = (event: SelectChangeEvent) => {
        const choice = event.target.value;
        if (choice === OTHER_CHOICE) {
            setFreeText(true);
            return;
        }
        const preset = presets.find((candidate) => candidate.singular === choice);
        if (!preset) return;
        setSingular(preset.singular);
        setPlural(preset.plural);
        setFreeText(false);
        setValidationError('');
    };

    const refreshCells = useCallback(async () => {
        try {
            setCells(await loadCells());
            setLoadFailed(false);
        } catch (error: any) {
            reportError(error);
        }
    }, [loadCells, reportError]);

    const handleSave = async () => {
        if (!changed) return;
        const trimmedSingular = singular.trim();
        const trimmedPlural = plural.trim();
        if (!isValidLabel(trimmedSingular) || !isValidLabel(trimmedPlural)) {
            setValidationError(translate('resources.sites.kioskSettings.lengthError'));
            return;
        }
        setValidationError('');
        setBusy(true);
        try {
            await apiClient.put(`${settingsUrl}/${encodeURIComponent(SINGULAR_KEY)}`, { value: trimmedSingular });
            await apiClient.put(`${settingsUrl}/${encodeURIComponent(PLURAL_KEY)}`, { value: trimmedPlural });
            notify(translate('resources.sites.kioskSettings.saved'), { type: 'success' });
        } catch (error: any) {
            reportError(error);
        } finally {
            setBusy(false);
            await refreshCells();
        }
    };

    const handleLabelKeyDown = (event: React.KeyboardEvent) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        void handleSave();
    };

    const handleClear = async () => {
        setValidationError('');
        setBusy(true);
        try {
            if (singularCell) await apiClient.delete(`${settingsUrl}/${encodeURIComponent(SINGULAR_KEY)}`);
            if (pluralCell) await apiClient.delete(`${settingsUrl}/${encodeURIComponent(PLURAL_KEY)}`);
            notify(translate('resources.sites.kioskSettings.cleared'), { type: 'success' });
        } catch (error: any) {
            reportError(error);
        } finally {
            setBusy(false);
            await refreshCells();
        }
    };

    if (!canRead) return null;

    const sourceKey = overrideCount === 2 ? 'site' : overrideCount === 1 ? 'partial' : 'inherited';

    const body = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (loadFailed) {
            return (
                <Typography variant="body2" color="error">
                    {translate('resources.sites.kioskSettings.loadError')}
                </Typography>
            );
        }
        if (!defaultSingular || !defaultPlural) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.sites.kioskSettings.unavailable')}
                </Typography>
            );
        }
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.sites.kioskSettings.intro')}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={translate(`resources.sites.kioskSettings.source.${sourceKey}`)} />
                    {overrideCount === 0 && (
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.sites.kioskSettings.inheritedHelper', {
                                label: defaultSingular,
                                plural: defaultPlural,
                            })}
                        </Typography>
                    )}
                </Box>

                {presets.length > 0 && (
                    <FormControl fullWidth>
                        <InputLabel id="site-kiosk-bay-label-preset">
                            {translate('resources.sites.kioskSettings.presetLabel')}
                        </InputLabel>
                        <Select
                            labelId="site-kiosk-bay-label-preset"
                            label={translate('resources.sites.kioskSettings.presetLabel')}
                            value={freeText ? OTHER_CHOICE : singular}
                            onChange={handlePresetChange}
                            disabled={!canWrite || busy}
                        >
                            {presets.map((preset) => (
                                <MenuItem key={preset.singular} value={preset.singular}>
                                    {`${preset.singular} / ${preset.plural}`}
                                </MenuItem>
                            ))}
                            <MenuItem value={OTHER_CHOICE}>{translate('resources.sites.kioskSettings.other')}</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {(freeText || presets.length === 0) && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                        <TextField
                            label={translate('resources.sites.kioskSettings.singular')}
                            value={singular}
                            onChange={(event) => {
                                setSingular(event.target.value);
                                setValidationError('');
                            }}
                            onKeyDown={handleLabelKeyDown}
                            helperText={translate('resources.sites.kioskSettings.singularHelper')}
                            disabled={!canWrite || busy}
                            fullWidth
                        />
                        <TextField
                            label={translate('resources.sites.kioskSettings.plural')}
                            value={plural}
                            onChange={(event) => {
                                setPlural(event.target.value);
                                setValidationError('');
                            }}
                            onKeyDown={handleLabelKeyDown}
                            helperText={translate('resources.sites.kioskSettings.pluralHelper')}
                            disabled={!canWrite || busy}
                            fullWidth
                        />
                    </Box>
                )}

                {validationError && (
                    <Typography variant="body2" color="error">
                        {validationError}
                    </Typography>
                )}

                {canWrite && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={busy || !changed}
                            sx={{
                                backgroundColor: '#00C853',
                                '&:hover': { backgroundColor: '#00A843' },
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {translate('resources.sites.kioskSettings.save')}
                        </Button>
                        {overrideCount > 0 && (
                            <Button onClick={handleClear} disabled={busy} sx={{ textTransform: 'none' }}>
                                {translate('resources.sites.kioskSettings.clear')}
                            </Button>
                        )}
                        {!changed && (
                            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                {translate('resources.sites.kioskSettings.unchanged')}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <SettingsIcon sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.sites.kioskSettings.title')}
                    </Typography>
                </Box>
                {body()}
            </CardContent>
        </Card>
    );
};

export const SiteKioskSettingsSection: React.FC = () => {
    const record = useRecordContext();
    if (!record?.id || !record.kioskAccessEnabled) return null;
    const siteId = String(record.id);
    return (
        <>
            <SiteKioskBayNumberWarning siteId={siteId} />
            <SiteKioskSettingsPanel siteId={siteId} />
            <SiteKioskChargeRoundingPanel siteId={siteId} />
            <SiteSoepayAccountPanel siteId={siteId} />
        </>
    );
};
